import * as React from 'react';
import { DataAttributes } from '../utils/DataAttributes';
import { makeTestId } from '../utils/Declarative';
import { InteractiveDomWrapper, InteractiveStyles } from '../utils/InteractiveDom';

export interface InteractiveLinkProps {
  /**
   * The href attribute on the anchor tag, defaults to '#'. This link will not actually be opened
   * when a user clicks because this widget calls `preventDefault` on `click` events. This prop is provided
   * for situations in which a real URL can be provided to a user to use later, e.g. to copy/paste into
   * an email.
   */
  href?: string;

  /** Fired when user clicks or presses ENTER on the anchor tag. `preventDefault` will be called before this. */
  onClick?: () => void;

  /** Specifies style to be applied to the anchor tag. n.b. `textDecoration` will always be overridden. */
  style?: React.CSSProperties;

  /** Test id to be attached to the anchor tag. Will be suffixed with '-InteractiveLink'. */
  testId: string;

  /** The text which the anchor tag wraps. */
  text: string;
}

const underline: React.CSSProperties = { textDecoration: 'underline' };
const interactiveStyles: InteractiveStyles = {
  active: underline,
  focus: underline,
  hover: underline,
};

/**
 * The styling of this widget is not extensible for interactive states and does not match defined Tableau styles.
 *
 * @deprecated use `TextLinkWidget`
 */
export class InteractiveLinkWidget extends React.PureComponent<InteractiveLinkProps, {}> {
  private handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (this.props.onClick) {
      this.props.onClick();
    }
  }

  public render(): JSX.Element {
    const anchorProps: React.AnchorHTMLAttributes<HTMLAnchorElement> & DataAttributes = {
      'data-tb-test-id': makeTestId(this.props.testId, 'InteractiveLink'),
      href: this.props.href || '#',
      onClick: this.handleClick,
      style: { ...this.props.style, textDecoration: 'none' },
    };

    return (
      <InteractiveDomWrapper interactiveStyles={interactiveStyles}>
        <a {...anchorProps}>
          {this.props.text}
        </a>
      </InteractiveDomWrapper>
    );
  }
}
